/*  useNotificationToasts — one-shot in-app toasts.
    ────────────────────────────────────────────────────────────────
    iter100.155 — Fetches unseen notifications for the current user
    exactly once on mount, fires a `sonner` toast per item, then bulk-
    marks them all seen so they never re-fire.

    Used by CreatorShell (author portal) and AdminLayout in the future.
    Safe to call unconditionally — bails silently if the user isn't
    authenticated or the /notifications endpoint returns anything
    unexpected.
*/
import { useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";


const ICON_BY_TYPE = {
    blog_approved: "🎉",
    blog_rejected: "✏️",
    course_approved: "🎓",
    course_rejected: "✏️",
};


export function useNotificationToasts({ enabled = true } = {}) {
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;

        (async () => {
            try {
                const { data } = await api.get("/notifications/mine");
                if (cancelled) return;
                const items = data.items || [];
                if (items.length === 0) return;

                // Fire a toast per item, staggered so they stack cleanly.
                items.forEach((n, i) => {
                    setTimeout(() => {
                        if (cancelled) return;
                        const isReject = /rejected/.test(n.type || "");
                        const icon = ICON_BY_TYPE[n.type] || "🔔";
                        const opts = {
                            description: n.body,
                            duration: isReject ? 12000 : 7000,
                        };
                        if (n.link) {
                            opts.action = {
                                label: n.link.startsWith("/creator") ? "Open" : "View",
                                onClick: () => { window.location.href = n.link; },
                            };
                        }
                        if (isReject) toast.warning(`${icon} ${n.title}`, opts);
                        else toast.success(`${icon} ${n.title}`, opts);
                    }, i * 350);
                });

                // Bulk-mark seen after firing so refresh doesn't re-toast.
                try { await api.post("/notifications/seen-all"); } catch { /* non-fatal */ }
            } catch {
                /* silent — notifications are a nice-to-have */
            }
        })();

        return () => { cancelled = true; };
    }, [enabled]);
}
